import type {
  CreditCardWithCurrencies,
  DetailedCreditCard,
} from '@contracts/payment/CreditCard';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { TransactionResult } from '@contracts/payment/TransactionResult';
import type { CreditCardFields } from '@src/clients/financialSettings/fields/CreditCardFields';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import type { AlertingService } from '@src/observability/useAlerting';
import { CurrenciesSchema } from '@src/orders/domain/Currency';

import type {
  CreatePaymentResponse,
  SafeCharge,
  SafeChargeField,
} from '../../../nuvei';

type CreatePaymentArgs = {
  data: CreditCardFields & { cardFields: SafeChargeField };
  clientId: string;
  currency: TransactionCurrency;
  amount: number;
  safeCharge: SafeCharge;
  onError: () => void;
};

const NUVEI_PAYMENT_TIMEOUT_MS = 30000;

const getAuthorizationForOrderPreAuthTransaction = async ({
  clientId,
  currency,
  amount,
  saveCardOnFile = false,
}: {
  clientId?: string;
  currency: TransactionCurrency;
  amount: number;
  saveCardOnFile?: boolean;
}) => {
  const { data } = await AuthenticatedClient.post<{
    sessionToken: string;
  }>(`payment:preauthorize`, {
    clientId: saveCardOnFile ? clientId : undefined,
    amount: amount.toString(),
    currency,
  });

  return data;
};

const getAuthorizationForOrderPaymentTransaction = async ({
  clientId,
  currency,
  amount,
  saveCardOnFile = false,
}: {
  clientId?: string;
  currency: TransactionCurrency;
  amount: number;
  saveCardOnFile?: boolean;
}) => {
  const { data } = await AuthenticatedClient.post<{
    sessionToken: string;
  }>(`payment:pay`, {
    clientId: saveCardOnFile ? clientId : undefined,
    amount: amount.toString(),
    currency,
  });

  return data;
};

const createPreAuthPaymentWithCardToBeSaved = async (
  args: CreatePaymentArgs & {
    logger: AlertingService;
    onSuccess: (result: {
      sessionToken: string;
      transactionId: string;
      creditCardId: string;
      creditCardNumber: string;
    }) => void;
    onSavedWithError: ({
      errorCode,
      description,
      creditCardId,
      transactionId,
    }: {
      errorCode: string;
      description: string;
      transactionId: string;
      creditCardId: string;
    }) => void;
  },
) =>
  createPaymentWithCardToBeSaved({
    ...args,
    getAuthorization: getAuthorizationForOrderPreAuthTransaction,
  });

const createMakePaymentWithCardToBeSaved = async (
  args: CreatePaymentArgs & {
    logger: AlertingService;
    onSuccess: (result: {
      sessionToken: string;
      transactionId: string;
      creditCardId: string;
      creditCardNumber: string;
    }) => void;
    onSavedWithError: ({
      errorCode,
      description,
      creditCardId,
      transactionId,
    }: {
      errorCode: string;
      description: string;
      transactionId: string;
      creditCardId: string;
    }) => void;
  },
) =>
  createPaymentWithCardToBeSaved({
    ...args,
    getAuthorization: getAuthorizationForOrderPaymentTransaction,
  });

const createPaymentWithCardToBeSaved = async ({
  currency,
  safeCharge,
  amount,
  clientId,
  data,
  onError,
  onSuccess,
  onSavedWithError,
  logger,
  getAuthorization,
}: CreatePaymentArgs & {
  logger: AlertingService;
  onSuccess: (result: {
    sessionToken: string;
    transactionId: string;
    creditCardId: string;
    creditCardNumber: string;
  }) => void;
  onSavedWithError: ({
    errorCode,
    description,
    creditCardId,
    transactionId,
  }: {
    errorCode: string;
    description: string;
    transactionId: string;
    creditCardId: string;
  }) => void;
  getAuthorization: (params: {
    clientId?: string;
    currency: TransactionCurrency;
    amount: number;
    saveCardOnFile?: boolean;
  }) => Promise<{ sessionToken: string }>;
}) => {
  try {
    const { sessionToken } = await getAuthorization({
      clientId,
      amount,
      currency,
      saveCardOnFile: true,
    });

    const response = await createPaymentWithTimeout<CreatePaymentResponse>(
      safeCharge,
      buildPaymentInformation(sessionToken, data),
    );

    if (response.result === 'ERROR') {
      logger.error(
        `createPayment not approved: ${response.result}: ${response.errorDescription}`,
        response,
      );
      onError();
      return;
    }

    if (response.result === 'DECLINED') {
      onSavedWithError({
        transactionId: response.transactionId.toString(),
        creditCardId: response.userPaymentOptionId.toString(),
        errorCode: response.errCode,
        description: response.errorDescription,
      });
      return;
    }

    onSuccess({
      sessionToken,
      transactionId: response.transactionId.toString(),
      creditCardId: response.userPaymentOptionId.toString(),
      creditCardNumber: response.ccCardNumber.toString(),
    });
  } catch (error) {
    logger.error('Payment operation failed or timed out', error as Error);
    onError();
  }
};

const createPaymentWithSingleUseCard = async ({
  currency,
  safeCharge,
  amount,
  clientId,
  data,
  onError,
  onSuccess,
  onPaymentFailed,
  getAuthorization,
}: CreatePaymentArgs & {
  onPaymentFailed: ({
    errorCode,
    description,
    status,
  }: {
    errorCode: string;
    description: string;
    status: string;
  }) => void;
  onSuccess: (result: {
    sessionToken: string;
    transactionId: string;
    creditCardNumber: string;
  }) => void;
  getAuthorization: (params: {
    clientId?: string;
    currency: TransactionCurrency;
    amount: number;
    saveCardOnFile?: boolean;
  }) => Promise<{ sessionToken: string }>;
}) => {
  try {
    const { sessionToken } = await getAuthorization({
      clientId,
      amount,
      currency,
      saveCardOnFile: false,
    });

    const response = await createPaymentWithTimeout<CreatePaymentResponse>(
      safeCharge,
      buildPaymentInformation(sessionToken, data),
    );

    if (response.result !== 'APPROVED') {
      onPaymentFailed({
        status: response.result,
        errorCode: response.errCode,
        description: response.errorDescription,
      });
      return;
    }

    onSuccess({
      sessionToken,
      transactionId: response.transactionId.toString(),
      creditCardNumber: response.ccCardNumber.toString(),
    });
  } catch {
    onError();
  }
};

const buildPaymentInformation = (
  sessionToken: string,
  data: CreditCardFields & { cardFields: SafeChargeField },
) => ({
  sessionToken,
  cardHolderName: data.nameOnCard,
  paymentOption: data.cardFields,
  isRebilling: '0' as const,
  billingAddress: {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    country: data.countryCode,
    address: data.address,
    zip: data.zip,
    city: data.city,
    state: data.state,
  },
});

const createPaymentWithTimeout = <T>(
  safeCharge: SafeCharge,
  paymentParams: Parameters<SafeCharge['createPayment']>[0],
  timeoutMs: number = NUVEI_PAYMENT_TIMEOUT_MS,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(`Nuvei payment operation timed out after ${timeoutMs}ms`),
      );
    }, timeoutMs);

    try {
      safeCharge.createPayment(paymentParams, (response) => {
        clearTimeout(timeoutId);
        resolve(response as T);
      });
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
};

export const PaymentClient = {
  preauthorizeWithExistingCard: async ({
    creditCardId,
    amount,
    clientId,
    orderId,
    currency,
  }: {
    creditCardId: string;
    amount: number;
    clientId: string;
    orderId: string;
    currency: TransactionCurrency;
  }) => {
    const response = await AuthenticatedClient.post<TransactionResult>(
      `cards/${creditCardId}:preauthorize`,
      {
        currency,
        amount: amount.toString(),
        clientId,
        orderId,
      },
    );
    return response.data;
  },
  getCreditCards: async ({
    clientId,
    currency,
  }: {
    clientId: string;
    currency: TransactionCurrency;
  }) => {
    const { data } = await AuthenticatedClient.get<DetailedCreditCard[]>(
      `/payment/${clientId}/creditCards?currency=${currency}`,
    );

    return data;
  },
  getAllCreditCards: async ({ clientId }: { clientId: string }) => {
    const { data } = await AuthenticatedClient.get<CreditCardWithCurrencies[]>(
      `/payment/${clientId}/creditCards:listAll`,
    );

    return data;
  },
  getMerchantInformation: async ({
    currency,
  }: {
    currency: TransactionCurrency;
  }) => {
    const { data } = await AuthenticatedClient.get<{
      merchantId: string;
      merchantSiteId: string;
    }>(`payment/merchantDetails?currency=${currency}`);

    return {
      merchantId: data.merchantId,
      merchantSiteId: data.merchantSiteId,
    };
  },
  listAvailableCurrencies: async (): Promise<TransactionCurrency[]> => {
    const { data } = await AuthenticatedClient.get<string[]>(
      `payment/available-currencies`,
    );

    return CurrenciesSchema.validateSync(data) as TransactionCurrency[];
  },
  createPreAuthPaymentWithCardToBeSaved,
  createPreAuthPaymentWithSingleUseCard: async (
    args: CreatePaymentArgs & {
      onPreAuthFailed: ({
        errorCode,
        description,
        status,
      }: {
        errorCode: string;
        description: string;
        status: string;
      }) => void;
      onSuccess: (result: {
        sessionToken: string;
        transactionId: string;
        creditCardNumber: string;
      }) => void;
    },
  ) =>
    createPaymentWithSingleUseCard({
      ...args,
      onPaymentFailed: args.onPreAuthFailed,
      getAuthorization: getAuthorizationForOrderPreAuthTransaction,
    }),
  createMakePaymentWithCardToBeSaved,
  createMakePaymentWithSingleUseCard: async (
    args: CreatePaymentArgs & {
      onMakePaymentFail: ({
        errorCode,
        description,
        status,
      }: {
        errorCode: string;
        description: string;
        status: string;
      }) => void;
      onSuccess: (result: {
        sessionToken: string;
        transactionId: string;
        creditCardNumber: string;
      }) => void;
    },
  ) =>
    createPaymentWithSingleUseCard({
      ...args,
      onPaymentFailed: args.onMakePaymentFail,
      getAuthorization: getAuthorizationForOrderPaymentTransaction,
    }),
  saveTransaction: async ({
    transactionId,
    orderId,
    clientId,
    creditCardNumber,
    amount,
    currency,
    sessionToken,
    transactionType,
  }: {
    sessionToken: string;
    transactionId: string;
    orderId: string;
    clientId: string;
    creditCardNumber: string;
    amount: number;
    currency: TransactionCurrency;
    transactionType: 'PREAUTHORIZATION' | 'PAYMENT';
  }) => {
    await AuthenticatedClient.post(`payment:saveTransaction`, {
      transactionId,
      orderId,
      clientId,
      creditCardNumber,
      amount: amount.toString(),
      currency,
      sessionToken,
      transactionType,
    });
  },
  async saveCreditCardRegistration({
    clientId,
    transactionId,
    creditCardId,
  }: {
    clientId: string;
    transactionId: string;
    creditCardId: string;
  }) {
    await AuthenticatedClient.post(
      `payment/${clientId}/saveCreditCardRegistrationTransaction`,
      {
        transactionId,
        creditCardId,
      },
    );
  },
  payWithExistingCard: async ({
    creditCardId,
    amount,
    clientId,
    orderId,
    currency,
  }: {
    creditCardId: string;
    amount: number;
    clientId: string;
    orderId: string;
    currency: TransactionCurrency;
  }) => {
    await AuthenticatedClient.post(`cards/${creditCardId}:pay`, {
      currency,
      amount: amount.toString(),
      clientId,
      orderId,
    });
  },
  settle: async ({
    amount,
    transactionId,
  }: {
    amount: number;
    transactionId: string;
  }) => {
    await AuthenticatedClient.post(`transactions/${transactionId}:settle`, {
      amount: amount.toString(),
    });

    return { amount };
  },
  deleteCreditCard: async (
    clientId: string,
    creditCardId: string,
    currency: TransactionCurrency,
  ) => {
    return await AuthenticatedClient.delete(
      `/payment/${clientId}/creditCards/${creditCardId}`,
      {
        data: { currency },
      },
    );
  },
};
